import {showError, showSuccess} from '@phonectas/unifon-ui-kit';
import {getI18n} from 'react-i18next';
import {getAliasHeader, getAliasHeaderFormData} from 'services/constants';
import {createState} from './createState';
import {LIMIT_PAYLOAD} from 'common/constants';
import {bmoApiUrl} from 'services/services';
import {calculateUserCases} from 'services/bmo.utils';
import {TicketStatus} from 'services/types';
import {
	// BmoAddEmailRequest,
	BmoAddEmailResponse,
	BmoAddNote,
	BmoNoteResponse,
	BmoStatus,
	BmoTicketById,
	BmoTicketsList,
	PostAttachmentsResponse,
} from 'common/types/store/bmoTypes';

// Error bodies are not always JSON (e.g. proxy 413/502 pages), so never let parsing hide the real status.
const readJson = async <T>(response: Response): Promise<T | null> => {
	try {
		return await response.json() as T;
	} catch {
		return null;
	}
};

const getErrorMessage = (response: Response, json: {message?: string} | null): string =>
	json?.message || getI18n().t('Telenor - Error-code: {{status}}', {status: response.status}) as string;

// The backend answers 400 when Telenor created the ticket but a later step (attachments, db, emails) failed.
const PARTIAL_CREATE_MARKER = 'has been created in telenor side';

let listController: AbortController | null = null;

interface Cases {
    usersCases: {
        open: number;
        closed: number;
        held: number;
        total: number;
    };
    totalCases: {
        open: number;
        closed: number;
        held: number;
        total: number;
    };
}

interface BMOState {
    ticketById: BmoTicketById | null;
    ticketsList: BmoTicketsList[] | null;
    searchList: BmoTicketsList[] | null;
    isLoading: boolean;
    hasMoreCases: boolean;
    activeUser: string | null;
    catalogId: string | null;
    statuses: BmoStatus[] | null;
    cases: Cases;
	error: boolean;
}

interface BmoStore extends BMOState {
    [key: string]: unknown;
    setActiveUser: (user: string) => void;
	clearError: () => void;
    setCatalogId: (id: string) => void;
    fetchAllCases: (
        limit?: number,
        query?: string,
        statusIds?: number[],
        myCases?: string
    ) => Promise<void>;
    searchCases: (limit?: number, query?: string) => Promise<void>;
    getStatuses: () => Promise<void>;
    postTicket: (data: FormData, onPostSuccess?: () => void) => Promise<BmoTicketById>;
    addNote: (id: string, data: BmoAddNote, onSuccess?: () => void) => Promise<BmoNoteResponse>;
    updateNote: (caseId: string, noteId: string, data: string, onSuccess?: () => void) => Promise<BmoNoteResponse>;
    getTicketId: (id: string, onFetchOk?: () => void, onError?: () => void) => Promise<void>;
    postAttachments: (id: string, data: FormData, onPostSuccess?: () => void) => Promise<PostAttachmentsResponse>;
    postTicketWithAttach: (data: FormData, onPostSuccess?: () => void) => Promise<PostAttachmentsResponse>;
    addEmail: (id: string, data: string[], onAddEmailSuccess?: () => void) => Promise<BmoAddEmailResponse>;
}

const useBmoStore = createState<BmoStore>('BmoStore', (set, get) => ({
	ticketById: null,
	ticketsList: null,
	searchList: null,
	isLoading: false,
	hasMoreCases: false,
	activeUser: null,
	catalogId: null,
	statuses: null,
	error: false,
	cases: {
		usersCases: {
			open: 0,
			closed: 0,
			held: 0,
			total: 0,
		},
		totalCases: {
			open: 0,
			closed: 0,
			held: 0,
			total: 0,
		},
	},
	clearError: () =>
		set((state) => ({...state, error: false}), false, 'BMO/clearError'),
	setActiveUser: (user) =>
		set((state) => ({...state, activeUser: user}), false, 'BMO/setActiveUser'),
	setCatalogId: (id) =>
		set((state) => ({...state, catalogId: id}), false, 'BMO/setCatalogId'),
	fetchAllCases: async (limit = LIMIT_PAYLOAD, query, statusIds = [], myCases) => {
		// Only the latest list request may update the store; typing in search fires them back to back.
		listController?.abort();
		const controller = new AbortController();
		listController = controller;
		try {
			set((state) => ({...state, isLoading: true}), false, 'BMO/fetchAllCases');
			const user = get().activeUser || '';
			const response = await fetch(`${bmoApiUrl}tickets/list`, {
				headers: getAliasHeader(),
				method: 'POST',
				signal: controller.signal,
				body: JSON.stringify({
					limit: limit,
					search_text: query || '',
					status_ids: statusIds,
					created_by: myCases || '',
				}),
			});
			const json = await readJson<BmoTicketsList[] & {message?: string}>(response);
			if (response.ok && Array.isArray(json)) {
				set((state) => ({
					...state,
					ticketsList: json,
					hasMoreCases: json.length >= limit,
					cases: calculateUserCases(json as unknown as TicketStatus[], user),
				}), false, 'BMO/fetchAllCases/setState');
			} else {
				showError({message: getErrorMessage(response, json)});
			}
		} catch (err) {
			if (controller.signal.aborted) return;
			console.error('BMO fetchAllCases failed', err);
			showError({message: getI18n().t('Error fetching cases') as string});
		} finally {
			if (listController === controller) {
				listController = null;
				set((state) => ({...state, isLoading: false}), false, 'BMO/fetchAllCases/isLoading');
			}
		}
	},
	searchCases: async (limit = LIMIT_PAYLOAD, query) => {
		try {
			set((state) => ({...state, isLoading: true}), false, 'BMO/searchCases');
			const response = await fetch(`${bmoApiUrl}tickets/list`, {
				headers: getAliasHeader(),
				method: 'POST',
				body: JSON.stringify({
					limit: limit,
					search_text: query,
				}),
			});
			const json = await readJson<BmoTicketsList[] & {message?: string}>(response);
			if (response.ok && Array.isArray(json)) {
				set((state) => ({
					...state,
					searchList: json,
				}), false, 'BMO/searchCases/setState');
			} else {
				showError({message: getErrorMessage(response, json)});
			}
		} catch (err) {
			console.error('BMO searchCases failed', err);
			showError({message: getI18n().t('Error fetching cases') as string});
		} finally {
			set((state) => ({...state, isLoading: false}), false, 'BMO/searchCases/isLoading');
		}
	},
	getStatuses: async () => {
		try {
			set((state) => ({...state, isLoading: true}), false, 'BMO/GET_STATUSES');
			const response = await fetch(`${bmoApiUrl}tickets/statuses`, {
				headers: getAliasHeader(),
				method: 'GET',
			});
			const json = await readJson<BmoStatus[] & {message?: string}>(response);
			if (response.ok && json) {
				set((state) => ({
					...state,
					statuses: json,
				}), false, 'BMO/GET_STATUSES/setState');
			} else {
				showError({message: getErrorMessage(response, json)});
			}
		} catch (err) {
			console.error('BMO getStatuses failed', err);
			showError({message: getI18n().t('Error fetching statuses') as string});
		} finally {
			set((state) => ({...state, isLoading: false}), false, 'BMO/GET_STATUSES/isLoading');
		}
	},
	postTicket: async (data, onPostSuccess): Promise<BmoTicketById> => {
		try {
			set((state) => ({...state, isLoading: true}), false, 'BMO/postTicket');
			const response = await fetch(`${bmoApiUrl}tickets`, {
				headers: getAliasHeaderFormData(),
				method: 'POST',
				body: data,
			});
			const json = await readJson<BmoTicketById>(response);
			if (response.ok && json) {
				showSuccess({
					message: getI18n().t('Ticket {{id}} successfuly created', {
						id: json.id,
					}) as string,
				});
				if (onPostSuccess) {
					onPostSuccess();
				}
				return json;
			}
			showError({message: getErrorMessage(response, json)});
			throw new Error(`Failed to create ticket: ${response.status}`);
		} catch (err) {
			console.error('BMO postTicket failed', err);
			throw err;
		} finally {
			set((state) => ({...state, isLoading: false}), false, 'BMO/postTicket/isLoading');
		}
	},
	addNote: async (id, data, onSuccess) => {
		try {
			set((state) => ({...state, isLoading: true}), false, 'BMO/addNote');
			const response = await fetch(`${bmoApiUrl}tickets/${id}/notes`, {
				headers: getAliasHeader(),
				method: 'POST',
				body: JSON.stringify(data),
			});
			const json = await readJson<BmoNoteResponse>(response);
			if (response.ok && json) {
				showSuccess({
					message: getI18n().t('Note successfuly created') as string,
				});
				if (onSuccess) {
					onSuccess();
				}
				return json;
			}
			showError({message: getErrorMessage(response, json)});
			throw new Error(`Failed to create note on ticket ${id}: ${response.status}`);
		} catch (err) {
			console.error(err);
			throw err;
		} finally {
			set((state) => ({...state, isLoading: false}), false, 'BMO/addNote/isLoading');
		}
	},
	updateNote: async (caseId, noteId, data, onSuccess) => {
		try {
			set((state) => ({...state, isLoading: true}), false, 'BMO/updateNote');
			const response = await fetch(`${bmoApiUrl}tickets/${caseId}/notes/${noteId}`, {
				headers: getAliasHeader(),
				method: 'PUT',
				body: JSON.stringify({text: data}),
			});
			const json = await readJson<BmoNoteResponse>(response);
			if (response.ok && json) {
				showSuccess({
					message: getI18n().t('Note successfuly updated') as string,
				});
				if (onSuccess) {
					onSuccess();
				}
				return json;
			}
			showError({message: getErrorMessage(response, json)});
			throw new Error(`Failed to update note ${noteId} on ticket ${caseId}: ${response.status}`);
		} catch (err) {
			console.error(err);
			throw err;
		} finally {
			set((state) => ({...state, isLoading: false}), false, 'BMO/updateNote/isLoading');
		}
	},
	getTicketId: async (id, onFetchOk, onError) => {
		try {
			set((state) => ({...state, isLoading: true}), false, 'BMO/getTicketId');
			const response = await fetch(`${bmoApiUrl}tickets/${id}`, {
				headers: getAliasHeader(),
				method: 'GET',
			});
			const json = await readJson<BmoTicketById>(response);
			if (response.ok && json) {
				set((state) => ({
					...state,
					ticketById: json,
				}), false, 'BMO/getTicketId/setState');
				if (onFetchOk) {
					onFetchOk();
				}
			} else {
				showError({
					message: getI18n().t('Telenor - id not found: {{id}}', {
						id: id,
					}) as string,
				});
				if (onError) {
					onError();
				}
			}
		} catch (err) {
			console.error(`BMO getTicketId ${id} failed`, err);
			showError({message: getI18n().t('Error fetching case {{id}}', {id}) as string});
			if (onError) {
				onError();
			}
		} finally {
			set((state) => ({...state, isLoading: false}), false, 'BMO/getTicketId/isLoading');
		}
	},
	postAttachments: async (id, data: FormData, onPostSuccess) => {
		try {
			set((state) => ({...state, isLoading: true}), false, 'BMO/postAttachments');
			const response = await fetch(`${bmoApiUrl}tickets/${id}/attachments`, {
				headers: getAliasHeaderFormData(),
				method: 'POST',
				body: data,
			});
			const json = await readJson<PostAttachmentsResponse>(response);
			if (response.ok) {
				showSuccess({
					message: getI18n().t('Attachment successfuly sent') as string,
				});
				if (onPostSuccess) {
					onPostSuccess();
				}
				return json ?? true;
			}
			showError({message: getErrorMessage(response, typeof json === 'object' ? json : null)});
			throw new Error(`Failed to post attachments to ticket ${id}: ${response.status}`);
		} catch (err) {
			console.error(err);
			throw err;
		} finally {
			set((state) => ({...state, isLoading: false}), false, 'BMO/postAttachments/isLoading');
		}
	},
	postTicketWithAttach: async (data: FormData, onPostSuccess) => {
		try {
			set((state) => ({...state, isLoading: true}), false, 'BMO/postTicket');
			const response = await fetch(`${bmoApiUrl}tickets/withattachments`, {
				headers: getAliasHeaderFormData(),
				method: 'POST',
				body: data,
			});
			const json = await readJson<{id?: string; message?: string}>(response);
			if (response.ok) {
				set((state) => ({...state, error: false}));
				showSuccess({
					message: getI18n().t('Ticket {{id}} successfuly created', {
						id: json?.id,
					}) as string,
				});
				if (onPostSuccess) {
					onPostSuccess();
				}
				return true;
			}
			const message = getErrorMessage(response, json);
			showError({message});
			if (json?.message?.includes(PARTIAL_CREATE_MARKER)) {
				// The ticket exists at Telenor: reset the form so the user does not create a duplicate.
				set((state) => ({...state, error: false}));
				if (onPostSuccess) {
					onPostSuccess();
				}
				return {message, status: 'error'};
			}
			set((state) => ({...state, error: true}));
			throw new Error(`Failed to create ticket: ${response.status}`);
		} catch (err) {
			console.error(err);
			throw err;
		} finally {
			set((state) => ({...state, isLoading: false}), false, 'BMO/postTicket/isLoading');
		}
	},
	addEmail: async (id, data, onAddEmailSuccess) => {
		try {
			set((state) => ({...state, isLoading: true}), false, 'BMO/addEmail');
			const response = await fetch(`${bmoApiUrl}tickets/${id}/emails`, {
				headers: getAliasHeader(),
				method: 'POST',
				body: JSON.stringify({notification_emails: data}),
			});
			const json = await readJson<BmoAddEmailResponse>(response);
			if (response.ok && json) {
				showSuccess({
					message: getI18n().t('Email list successfuly updated') as string,
				});
				if (onAddEmailSuccess) {
					onAddEmailSuccess();
				}
				return json;
			}
			showError({message: getErrorMessage(response, json)});
			throw new Error(`Failed to update email list on ticket ${id}: ${response.status}`);
		} catch (err) {
			console.error(err);
			throw err;
		} finally {
			set((state) => ({...state, isLoading: false}), false, 'BMO/addEmail/isLoading');
		}
	},
}));

export default useBmoStore;
